import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";

export default function TermsAndConditions() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <div className="bg-white border-b sticky top-0 z-10 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 rounded-xl hover:bg-gray-100">
          <ArrowLeft size={20} />
        </button>
        <div className="flex items-center gap-2">
          <FileText size={20} className="text-blue-600" />
          <h1 className="font-bold text-lg">Terms &amp; Conditions</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6 text-sm text-gray-700">
        <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
          <p className="text-xs text-gray-500">Last updated: April 1, 2026</p>
          <p className="text-xs text-gray-500 mt-0.5">Effective date: April 1, 2026</p>
        </div>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">1. Acceptance of Terms</h2>
          <p>By accessing or using CampusSwap ("the Platform", "we", "us"), you agree to be bound by these Terms and Conditions and our Privacy Policy. If you do not agree to these terms, you must not use the platform.</p>
          <p className="mt-2">These Terms constitute a legally binding agreement between you and CampusSwap under the laws of India, including the Information Technology Act, 2000, the IT (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021, and the Consumer Protection Act, 2019.</p>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">2. Eligibility</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>You must be at least 16 years of age to use this platform</li>
            <li>You must be a current student, school-going student, or parent/guardian of a school student</li>
            <li>You must provide accurate, truthful, and complete information during registration</li>
            <li>One person may not maintain more than one account</li>
            <li>Accounts are non-transferable and personal to the registered user</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">3. Nature of Platform (Intermediary Status)</h2>
          <p className="mb-2">CampusSwap is an <strong>online intermediary platform</strong> as defined under Section 2(1)(w) of the Information Technology Act, 2000. We provide a technology platform for peer-to-peer transactions between students and parents.</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>CampusSwap does <strong>not</strong> buy, sell, or own any items listed on the platform</li>
            <li>CampusSwap does <strong>not</strong> process payments or handle money between users</li>
            <li>All transactions are directly between the buyer and seller</li>
            <li>CampusSwap is not a party to any transaction and is not responsible for the quality, safety, legality, or delivery of items</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">4. User Responsibilities</h2>
          <p className="mb-2">By using the platform, you agree to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Post only items you personally own and genuinely intend to sell or exchange</li>
            <li>Provide accurate descriptions, photos, and prices for all listings</li>
            <li>Honour commitments made to buyers or sellers through the platform</li>
            <li>Communicate honestly and respectfully with other users</li>
            <li>Meet buyers/sellers only in safe, public locations on campus</li>
            <li>Not share your account credentials with any other person</li>
            <li>Report any suspicious, fraudulent, or abusive content using the report feature</li>
            <li>Comply with all applicable laws of India in your use of the platform</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">5. Prohibited Content and Activities</h2>
          <p className="mb-2">The following are <strong>strictly prohibited</strong> on CampusSwap:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Illegal items:</strong> Weapons, drugs, narcotics, alcohol, tobacco, counterfeit goods, or any item whose sale is prohibited under Indian law</li>
            <li><strong>Stolen goods:</strong> Listing or selling items that are stolen or obtained through illegal means</li>
            <li><strong>Fraudulent listings:</strong> Fake, misleading, duplicate, or non-existent items</li>
            <li><strong>Adult content:</strong> Pornographic, obscene, or sexually explicit material</li>
            <li><strong>Harmful content:</strong> Content that promotes violence, hatred, discrimination, or self-harm</li>
            <li><strong>Personal data:</strong> Sharing other users' personal information without consent</li>
            <li><strong>Spam and phishing:</strong> Unsolicited messages, scam links, or impersonation of other users</li>
            <li><strong>Platform manipulation:</strong> Creating fake accounts to manipulate listings, reviews, or reports</li>
            <li><strong>Intellectual property violations:</strong> Listing or distributing pirated software, books, movies, or other copyrighted material</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">6. Transactions and Payments</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>CampusSwap does not facilitate, process, or guarantee any payment</li>
            <li>Users arrange payment directly and offline between themselves</li>
            <li>We strongly advise against advance payments to unknown users</li>
            <li>CampusSwap is not liable for any financial loss arising from transactions</li>
            <li>In case of dispute, users must resolve it directly or contact our Grievance Officer</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">7. ID Verification</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>Users may be required to submit a college or school ID for verification</li>
            <li>Submitting a forged, altered, or fake ID is a violation of these Terms and may constitute an offence under the IT Act, 2000</li>
            <li>Verified status may be revoked if fraudulent documents are discovered</li>
            <li>ID images are used solely for verification and deleted within 30 days of review</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">8. Community Moderation</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>Users can report listings they believe are fraudulent or violate these Terms</li>
            <li>A listing reported by 3 or more users will be automatically hidden pending review</li>
            <li>An account receiving 5 or more reports across all listings will be automatically suspended pending review</li>
            <li>CampusSwap reserves the right to remove any content and suspend any account without prior notice if it violates these Terms</li>
            <li>Suspended users may appeal by contacting our Grievance Officer</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">9. Intellectual Property</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>The CampusSwap name, logo, app design, and original content are our intellectual property and protected under Indian copyright and trademark law</li>
            <li>You retain ownership of photos and descriptions you post</li>
            <li>By posting content, you grant CampusSwap a non-exclusive, royalty-free licence to display, reproduce, and distribute that content on the platform for the purpose of providing our services</li>
            <li>You confirm that any content you post does not infringe the intellectual property rights of any third party</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">10. Safe Harbour (IT Act Section 79)</h2>
          <p>CampusSwap operates as an intermediary under Section 79 of the Information Technology Act, 2000. We are not liable for third-party information, data, or communication links made available or hosted on the platform, provided we:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Do not initiate the transmission or select the receiver</li>
            <li>Do not modify the information</li>
            <li>Act expeditiously to remove or disable access to unlawful content upon receiving actual knowledge or notice</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">11. Limitation of Liability</h2>
          <p className="mb-2">To the maximum extent permitted by law:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>CampusSwap is provided on an "as is" and "as available" basis without warranties of any kind</li>
            <li>We are not liable for any direct, indirect, incidental, or consequential loss arising from your use of the platform</li>
            <li>We are not responsible for the conduct of any user, the quality of any item, or the outcome of any transaction</li>
            <li>Our total liability to you for any claim shall not exceed ₹500 (five hundred rupees)</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">12. Account Termination</h2>
          <p>We reserve the right to suspend, restrict, or permanently terminate any account that:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Violates these Terms or our community guidelines</li>
            <li>Posts fraudulent, illegal, or harmful content</li>
            <li>Engages in harassment, abuse, or manipulation of other users</li>
            <li>Submits false identity documents</li>
            <li>Receives repeated reports from the community</li>
          </ul>
          <p className="mt-2">You may delete your own account at any time from Profile → Delete Account.</p>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">13. Grievance Redressal (IT Rules 2021)</h2>
          <p className="mb-2">In accordance with Rule 3(2) of the IT (Intermediary Guidelines) Rules, 2021, we have appointed a Grievance Officer to address user complaints:</p>
          <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 space-y-1">
            <p><strong>Grievance Officer:</strong> CampusSwap Support Team</p>
            <p><strong>Email:</strong> <a href="mailto:campusswap.official@gmail.com" className="text-blue-600">campusswap.official@gmail.com</a></p>
            <p><strong>Acknowledgement:</strong> Within 24 hours of complaint receipt</p>
            <p><strong>Resolution:</strong> Within 30 days of complaint receipt</p>
          </div>
          <p className="mt-2 text-xs text-gray-500">Complaints regarding content that is in violation of Indian law will be addressed within 36 hours as required under the IT Rules, 2021.</p>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">14. Governing Law and Jurisdiction</h2>
          <p>These Terms are governed by and construed in accordance with the laws of India. Any disputes arising from these Terms or your use of the platform shall be subject to the exclusive jurisdiction of the competent courts in India. Users agree to attempt resolution through our Grievance Officer before approaching courts.</p>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">15. Changes to Terms</h2>
          <p>We reserve the right to modify these Terms at any time. Changes will be notified through the app. Continued use of the platform after notification constitutes acceptance of the revised Terms.</p>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">16. Contact</h2>
          <p>For questions about these Terms or to file a grievance:</p>
          <div className="mt-2 space-y-1">
            <p><strong>Email:</strong> <a href="mailto:campusswap.official@gmail.com" className="text-blue-600">campusswap.official@gmail.com</a></p>
            <p><strong>In-app:</strong> Profile → Help &amp; Support</p>
          </div>
        </section>
      </div>
    </div>
  );
}
